import mammoth from 'mammoth';
import DOMPurify from 'dompurify';

export interface ParsedDocx {
  html: string;
}

/** Convert a `.docx` blob to sanitized preview HTML.
 *
 * `mammoth` only emits a known-safe subset of tags (p, headings, lists,
 * tables, b/i/u, a, img as data URIs) — no script-capable elements — but the
 * output is still run through DOMPurify before render, same as this
 * codebase's other untrusted-HTML path (`sanitizeEmailHtml`), rather than
 * trusting the parser alone. Legacy `.doc` (binary OLE2, not OOXML) is not
 * supported by mammoth and stays on the download-only card — see
 * `resolveFilePreviewKind`.
 *
 * Deliberately does NOT set a custom `ALLOWED_URI_REGEXP` — DOMPurify's
 * default already excludes `data:` from it, and instead allows `data:` only
 * via its separate `DATA_URI_TAGS` carve-out (`img`/`audio`/`video`/etc.,
 * `src` only, never `href`). Widening the regex to include `data:` — a
 * previous version of this file did — would have made it match on every
 * attribute/tag, including a `<a href="data:text/html,...">` a malicious
 * `.docx` could smuggle through mammoth's own hyperlink support, bypassing
 * the tag restriction the default carve-out enforces (Cursor Bugbot,
 * OMNI-839 PR review).
 *
 * mammoth's `<a href>` tags are plain — no `target`/`rel` — so a hyperlink in
 * a rendered .docx would navigate the whole console away from the slide-out
 * panel instead of opening in a new tab, unlike every other external link in
 * ask-moo (Cursor Bugbot). Rewritten via a DOMParser walk, not a DOMPurify
 * hook, so this stays local to this call rather than mutating shared
 * sanitizer state other DOMPurify.sanitize() callers in the app could race
 * against.
 *
 * Only rewrites a link whose `href` is NOT a same-document fragment
 * (`#...`). mammoth also emits `#...` links for a document's own TOC,
 * footnotes, and cross-references — those need to stay in the preview and
 * jump to the matching in-page anchor, not pop a new console tab pointed at
 * nothing useful (Cursor Bugbot, follow-up on the fix above). */
export async function parseDocx(blob: Blob): Promise<ParsedDocx> {
  const arrayBuffer = await blob.arrayBuffer();
  const { value } = await mammoth.convertToHtml({ arrayBuffer });
  const sanitized = DOMPurify.sanitize(value, {
    FORBID_TAGS: [
      'script',
      'object',
      'embed',
      'applet',
      'iframe',
      'frame',
      'style',
    ],
    FORBID_ATTR: ['onerror', 'onload', 'onclick'],
  });
  const doc = new DOMParser().parseFromString(sanitized, 'text/html');
  doc.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    if (href.startsWith('#')) return;
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  return { html: doc.body.innerHTML };
}
